import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Debug mode for development
const DEBUG_MODE = process.env.NODE_ENV === 'development';

declare global {
  interface Window {
    mixpanel: any;
  }
}

const Mixpanel = () => {
  const location = useLocation();

  // Track page views on route change
  useEffect(() => {
    if (window.mixpanel) {
      const pageData = {
        page_path: location.pathname + location.search,
        page_title: document.title,
        page_url: window.location.href
      };

      window.mixpanel.track('Page View', pageData);
      
      if (DEBUG_MODE) {
        console.log('Mixpanel: Page view tracked', pageData);
      }
    } else if (DEBUG_MODE) {
      console.log('Mixpanel: Not available for page view tracking');
    }
  }, [location]);

  return null;
};

// Custom tracking functions for events with V2.0 parameters
export const trackEventMp = (eventName: string, parameters?: Record<string, any>) => {
  if (window.mixpanel) {
    window.mixpanel.track(eventName, {
      event_category: 'engagement',
      event_label: eventName,
      app_version: '2.0',
      layout_version: 'interactive_preview',
      ...parameters,
    });
    if (DEBUG_MODE) console.log('Mixpanel: Event tracked', eventName, parameters);
  } else if (DEBUG_MODE) {
    console.log('Mixpanel: Not available, event queued:', eventName, parameters);
  }
};

export const trackConversionMp = (eventName: string, value?: number) => {
  if (window.mixpanel) {
    window.mixpanel.track(eventName, {
      event_category: 'conversion',
      event_label: eventName,
      app_version: '2.0',
      layout_version: 'interactive_preview',
      value: value,
    });
    if (DEBUG_MODE) console.log('Mixpanel: Conversion tracked', eventName, value);
  } else if (DEBUG_MODE) {
    console.log('Mixpanel: Not available, conversion queued:', eventName, value);
  }
};

export const trackSubscriptionMp = (email: string, plan?: string) => {
  if (window.mixpanel) {
    // Identify the user
    window.mixpanel.identify(email);
    window.mixpanel.people.set({
      $email: email,
      subscription_plan: plan || 'trial',
      signup_date: new Date().toISOString()
    });

    window.mixpanel.track('Newsletter Signup', {
      event_category: 'conversion',
      event_label: 'newsletter_subscription',
      user_email: email,
      subscription_plan: plan || 'trial',
      app_version: '2.0',
      layout_version: 'interactive_preview',
    });
    if (DEBUG_MODE) console.log('Mixpanel: Subscription tracked', email, plan);
  } else if (DEBUG_MODE) {
    console.log('Mixpanel: Not available, subscription queued:', email, plan);
  }
};

// New V2.0 specific tracking functions
export const trackInteractivePreviewMp = (action: string, templateId?: string) => {
  if (window.mixpanel) {
    window.mixpanel.track('Interactive Preview Engagement', {
      event_category: 'preview_interaction',
      action: action,
      template_id: templateId,
      app_version: '2.0',
      engagement_type: 'preview_interaction',
    });
    if (DEBUG_MODE) console.log('Mixpanel: Interactive preview tracked', action, templateId);
  } else if (DEBUG_MODE) {
    console.log('Mixpanel: Not available, preview interaction queued:', action, templateId);
  }
};

export const trackConversionFunnelMp = (step: string, stepNumber: number) => {
  if (window.mixpanel) {
    window.mixpanel.track('Conversion Funnel', {
      event_category: 'funnel',
      funnel_step: step,
      step_number: stepNumber,
      app_version: '2.0',
      funnel_type: 'newsletter_signup_v2',
    });
    if (DEBUG_MODE) console.log('Mixpanel: Funnel step tracked', step, stepNumber);
  } else if (DEBUG_MODE) {
    console.log('Mixpanel: Not available, funnel step queued:', step, stepNumber);
  }
};

export const trackFormEngagementMp = (formType: string, engagement: string) => {
  if (window.mixpanel) {
    window.mixpanel.track('Form Engagement', {
      event_category: 'form_interaction',
      form_type: formType,
      engagement_type: engagement,
      app_version: '2.0',
    });
    if (DEBUG_MODE) console.log('Mixpanel: Form engagement tracked', formType, engagement);
  } else if (DEBUG_MODE) {
    console.log('Mixpanel: Not available, form engagement queued:', formType, engagement);
  }
};

export default Mixpanel;